/**
 * Whether this browser can actually run the 3D hero.
 *
 * Asked once, before the Canvas is ever mounted. A machine without WebGL — an
 * old device, a locked-down corporate browser, a headless crawler, a GPU
 * blocklisted by the driver — must get the static hero instead of a black box or
 * a thrown error, and it must get it on the first paint rather than after a
 * failed mount flashes.
 */
export function supportsWebGL(): boolean {
  if (typeof window === 'undefined') return false;

  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ??
      canvas.getContext('webgl') ??
      canvas.getContext('experimental-webgl');

    if (!gl) return false;

    // Losing the context immediately keeps the probe from holding a real GPU
    // context open for the lifetime of the page.
    const lose = (gl as WebGLRenderingContext).getExtension('WEBGL_lose_context');
    lose?.loseContext();

    return true;
  } catch {
    return false;
  }
}

/** True when the visitor has asked their OS to reduce motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
